import * as z from 'zod';

export const homerSendMessageFnName = 'homer_send_message' as const;

export const homerAdminReplyFnName = 'homer_admin_reply' as const;

/**
 * Remote Config top-level (ungrouped) boolean parameter that turns the Homer
 * chat widget on/off site-wide. Shared so the server (reads
 * `template.parameters[...]`) and the client (`getBoolean(rc, ...)`) agree on
 * the key. Lives here (not in @padspin/remote-config) so the Angular widget can
 * import it — the remote-config barrel isn't Angular-consumable.
 */
export const homerChatEnabledRemoteConfigKey = 'homer_chat_enabled' as const;

/** Fallback when Remote Config is unreachable or the parameter is unset. */
export const homerChatEnabledDefault = true;

/** `HttpsError.details.reason` when a send is refused by the per-user daily cap. */
export const homerRateLimitedReason = 'homer_rate_limited' as const;

/** `HttpsError.details.reason` when the `homer_chat_enabled` kill switch is off. */
export const homerDisabledReason = 'homer_chat_disabled' as const;

/** `HttpsError.details.reason` when the caller's account is not in the Homer beta. */
export const homerNotInBetaReason = 'homer_not_in_beta' as const;

/** Max user message length; enforced client-side (composer) and server-side. */
export const HOMER_MESSAGE_TEXT_MAX_LENGTH = 2000;

/** Max `page_url` length; the client truncates to this before sending. */
export const HOMER_PAGE_URL_MAX_LENGTH = 500;

/**
 * Max `page_context` length. Large enough for a compact listing summary plus a
 * few form fields; the client formats and bounds the context to this.
 */
export const HOMER_PAGE_CONTEXT_MAX_LENGTH = 2000;

export const HomerSendMessageRequestSchema = z.object({
  text: z.string().min(1).max(HOMER_MESSAGE_TEXT_MAX_LENGTH),
  /** Client-generated UUID; becomes the message doc ID for idempotent retries. */
  local_id: z.string().uuid(),
  page_url: z.string().max(HOMER_PAGE_URL_MAX_LENGTH).nullable().default(null),
  page_context: z
    .string()
    .max(HOMER_PAGE_CONTEXT_MAX_LENGTH)
    .nullable()
    .default(null),
});
export type HomerSendMessageRequest = z.infer<
  typeof HomerSendMessageRequestSchema
>;

export const HomerSendMessageResponseSchema = z.object({
  ok: z.literal(true),
  local_id: z.string(),
  /** False when the message was stored but no AI reply was produced (takeover on, or generation failed). */
  ai_replied: z.boolean(),
});
export type HomerSendMessageResponse = z.infer<
  typeof HomerSendMessageResponseSchema
>;

export const HomerAdminReplyRequestSchema = z.object({
  chat_uid: z.string().min(1),
  text: z.string().min(1).max(5000),
  local_id: z.string().uuid(),
});
export type HomerAdminReplyRequest = z.infer<
  typeof HomerAdminReplyRequestSchema
>;

export const HomerAdminReplyResponseSchema = z.object({
  ok: z.literal(true),
  local_id: z.string(),
});
export type HomerAdminReplyResponse = z.infer<
  typeof HomerAdminReplyResponseSchema
>;
